import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { ReviewHealthSummary, ReviewLogItem, ReviewLogsQueryParams } from '../types/reviews';
const API_URL = process.env.REACT_APP_API_URL || '/api/v1';

export const reviewsApi = createApi({
  reducerPath: 'reviewsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['ReviewSettings', 'ReviewLogs'],
  endpoints: (builder) => ({
    getAllReviewSettings: builder.query({
      query: () => '/reviews/settings',
      providesTags: ['ReviewSettings'],
    }),
    getReviewSettings: builder.query({
      query: (accountId) => `/reviews/settings/${accountId}`,
      providesTags: ['ReviewSettings'],
    }),
    updateReviewSettings: builder.mutation({
      query: ({ accountId, settings }) => ({
        url: `/reviews/settings/${accountId}`,
        method: 'PUT',
        body: settings,
      }),
      invalidatesTags: ['ReviewSettings'],
    }),
    getReviewLogs: builder.query<ReviewLogItem[], ReviewLogsQueryParams>({
      query: ({ accountId, status, minScore, hasReply, dateFrom, dateTo, limit, offset }) => {
        let url = `/reviews/logs?limit=${limit || 100}&offset=${offset || 0}`;
        if (accountId) url += `&account_id=${encodeURIComponent(accountId)}`;
        if (status) url += `&status=${status}`;
        if (minScore !== undefined) url += `&min_score=${minScore}`;
        if (hasReply !== undefined) url += `&has_reply=${hasReply}`;
        if (dateFrom) url += `&date_from=${encodeURIComponent(dateFrom)}`;
        if (dateTo) url += `&date_to=${encodeURIComponent(dateTo)}`;
        return url;
      },
      providesTags: ['ReviewLogs'],
    }),
    testAIPrompt: builder.mutation({
      query: (body) => ({
        url: '/reviews/test-prompt',
        method: 'POST',
        body,
      }),
    }),
    manualReply: builder.mutation({
      query: ({ reviewId, message }) => ({
        url: `/reviews/${reviewId}/reply`,
        method: 'POST',
        body: { message },
      }),
      invalidatesTags: ['ReviewLogs'],
    }),
    getReviewsHealth: builder.query<ReviewHealthSummary, void>({
      query: () => '/reviews/health',
      providesTags: ['ReviewSettings', 'ReviewLogs'],
    }),
    regenerateReviewReply: builder.mutation<ReviewLogItem, { reviewId: number }>({
      query: ({ reviewId }) => ({
        url: `/reviews/${reviewId}/regenerate-reply`,
        method: 'POST',
      }),
      invalidatesTags: ['ReviewLogs'],
    }),
    deleteReviewSettings: builder.mutation<void, string>({
      query: (accountId) => ({
        url: `/reviews/settings/${accountId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ReviewSettings'],
    }),
  }),
});

export const {
  useGetAllReviewSettingsQuery,
  useGetReviewsHealthQuery,
  useGetReviewSettingsQuery,
  useUpdateReviewSettingsMutation,
  useGetReviewLogsQuery,
  useTestAIPromptMutation,
  useManualReplyMutation,
  useRegenerateReviewReplyMutation,
  useDeleteReviewSettingsMutation,
} = reviewsApi;
